// @flow
import React, { Fragment, useContext, useRef } from 'react';

import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import { t } from 'Src/i18n';
import { AlternativePrice } from 'Components/widgets/alternative-proposals/alternative-price/AlternativePrice';
import ReactAppContext from 'Context/ReactAppContext';
import {
  PRICE_COUNTER_PROPOSAL_THRESHOLD,
  PRICE_COUNTER_PROPOSAL_SELLER_THRESHOLD,
} from 'Store/booking/proposals/constants';
import { AlternativeDateSuggestionHeader } from 'Components/widgets/alternative-proposals/alternative-date-suggestion/AlternativeDateSuggestionHeader';
import { AlternativeProposalHeader } from 'Components/widgets/alternative-proposals/alternative-proposal-header/AlternativeProposalHeader';
import { AlternativeDurationSuggestions } from 'Components/widgets/alternative-proposals/alternative-duration-suggestions/AlternativeDurationSuggestions';
import { AlternativeDateSuggestion } from './alternative-date-suggestion/AlternativeDateSuggestion';
import { AlternativeProposalsSeparatorDashed } from './AlternativeProposalsSeparatorDashed';
import { AlternativeResort } from './alternative-resort/AlternativeResort';

export type AlternativeProposalsProps = $ReadOnly<{|
  proposals: $ReadOnlyArray<Proposal>,
  i18n: CMSContent,
  resort: $ReadOnly<Resort>,
  onClose?: () => void,
  onSubmit: (args: *) => void,
  searchCriteria: ProposalSearchCriteria,
  scrollToBookingEngineEntryPoint?: () => void,
  bookingEngineEntryPoint?: string,
  goToBookingEngine?: () => Promise<*>,
  suggestions: BookingSuggestions,
  selectResortHandler?: (id: string, name: string) => mixed,
  onSelectNewDuration: ({ proposalIds: $ReadOnlyArray<string>, duration: number }) => void,
  disableAlternativePrice?: boolean,
|}>;

export function AlternativeProposals({
  searchCriteria: { arrivalDate, duration, adultsCount, departureCity, kidsCount },
  onClose,
  onSubmit,
  i18n,
  proposals,
  resort,
  scrollToBookingEngineEntryPoint,
  bookingEngineEntryPoint,
  goToBookingEngine,
  suggestions: { dates, resorts, durations },
  searchCriteria,
  selectResortHandler,
  onSelectNewDuration,
  disableAlternativePrice = false,
}: AlternativeProposalsProps) {
  const appContext = useContext(ReactAppContext);

  const separatorComponent = useRef(null);

  const proposal = proposals[0];
  const shouldDisplayAlternativePriceCriteria =
    !disableAlternativePrice &&
    proposal &&
    proposal.price &&
    proposal.price.gap &&
    proposal.price.gap > PRICE_COUNTER_PROPOSAL_THRESHOLD &&
    (appContext.isSeller || proposal.price.gap < PRICE_COUNTER_PROPOSAL_SELLER_THRESHOLD);

  const shouldDisplayAlternativePriceTitle =
    !disableAlternativePrice &&
    proposal &&
    proposal.price &&
    proposal.price.gap &&
    proposal.price.gap > PRICE_COUNTER_PROPOSAL_THRESHOLD;

  const seeOtherProposalsCallback = () => {
    if (separatorComponent.current) {
      separatorComponent.current.scrollIntoView();
    }
  };

  const atLeastOneDateCounterProposal = dates.length > 0;
  const atLeastOneResortCounterProposal = resorts.length > 0;
  const atLeastOneDurationCounterProposal = durations.length > 0;

  const hasSuggestions =
    atLeastOneDateCounterProposal ||
    atLeastOneResortCounterProposal ||
    atLeastOneDurationCounterProposal;

  return (
    <div
      data-testid="AlternativeProposalsModal"
      className="AlternativeProposalsModal rounded-small md:flex md:flex-col md:items-center"
    >
      {shouldDisplayAlternativePriceTitle ? (
        <AlternativeProposalHeader
          title={t('booking.counterProposalPopIn.rateCounterProposalTitle')}
          proposal={proposal}
        />
      ) : null}
      {shouldDisplayAlternativePriceCriteria && (
        <Fragment>
          <div className="AlternativeProposalsModal-content flex flex-col items-center px-8 w-full max-w-site">
            <AlternativePrice
              proposals={proposals}
              resort={resort}
              searchCriteria={searchCriteria}
              departureCities={i18n.bookingCriteria.departureCities}
              seeOtherProposalsCallback={seeOtherProposalsCallback}
              hasOtherProposals={hasSuggestions}
              onModify={
                onClose
                  ? () => {
                      onClose();

                      if (scrollToBookingEngineEntryPoint) {
                        scrollToBookingEngineEntryPoint();
                      }
                    }
                  : undefined
              }
              bookingEngineEntryPoint={bookingEngineEntryPoint}
              // $FlowFixMe
              continueToBookingEngine={goToBookingEngine}
            />
          </div>
          {hasSuggestions && <AlternativeProposalsSeparatorDashed ref={separatorComponent} />}
        </Fragment>
      )}
      {hasSuggestions && !shouldDisplayAlternativePriceTitle && !disableAlternativePrice ? (
        <AlternativeDateSuggestionHeader
          arrivalDate={arrivalDate}
          duration={duration}
          resort={resort}
        />
      ) : (
        <AlternativeProposalHeader
          title={t('booking.counterProposalPopIn.otherProposals')}
          iconWidth="3rem"
        />
      )}
      {atLeastOneDateCounterProposal && (
        <AlternativeDateSuggestion
          arrivalDate={arrivalDate}
          duration={duration}
          dates={dates}
          onSubmit={onSubmit}
          onClose={onClose}
          resort={resort}
        />
      )}
      {atLeastOneDateCounterProposal &&
        (atLeastOneDurationCounterProposal || atLeastOneResortCounterProposal) && (
          <AlternativeProposalsSeparatorDashed />
        )}
      {atLeastOneDurationCounterProposal && (
        <AlternativeDurationSuggestions
          onSelectNewDuration={onSelectNewDuration}
          durations={durations}
        />
      )}
      {atLeastOneDurationCounterProposal &&
        atLeastOneResortCounterProposal && <AlternativeProposalsSeparatorDashed />}
      {atLeastOneResortCounterProposal && (
        <Fragment>
          <AlternativeResort
            adultsNumber={adultsCount}
            arrivalDate={arrivalDate}
            departureCity={departureCity}
            duration={duration}
            i18n={i18n}
            kidsNumber={kidsCount}
            suggestions={resorts}
            scrollToBookingEngineEntryPoint={scrollToBookingEngineEntryPoint}
            selectResortHandler={selectResortHandler}
          />
        </Fragment>
      )}
      {!appContext.isCmta && (
        <Fragment>
          <div className="border-t border-solid mt-4 mb-2 md:mt-12 md:mb-8 w-full" />
          <div className="flex items-center mb-8 px-6">
            <Icon
              className="mr-3 lg:mr-2 flex"
              color={COLORS.BLUE}
              name={ICONS.PHONE_BUBBLE}
              width="4rem"
              hasRtl
            />
            <p className="font-bold text-lg text-blue">
              {t('booking.counterProposalPopIn.contactCallCenter')}{' '}
              <span className="AlternativeProposalsModal-callText">
                {t('booking.counterProposalPopIn.callCenterNumber')}
              </span>{' '}
              <span className="font-normal text-gray text-sm">
                {t('booking.counterProposalPopIn.callCost')}
              </span>
            </p>
          </div>
        </Fragment>
      )}
    </div>
  );
}
